/**
 * Issue #715 — a blank counter is not a nought.
 *
 * The Log Match form starts every counter blank, and leaving one blank is
 * how a player says the thing did not happen (#127 made the fields
 * blankable; this module is what stops the blank being thrown away at the
 * door). Until now `create()` ran each one through `toNum`, which turns ""
 * into `0`, so a match nobody bowled in was stored byte-for-byte as a spell
 * that took 0 for 0 off 0 — and the match list printed `0` for both. Once
 * saved, the difference was gone.
 *
 * Two functions, one on each side of the round trip:
 *
 *   - `counterOrNull` at submit: "" becomes `null`, a typed number becomes
 *     that number, and a typed `0` stays `0`. A recorded nought is a real
 *     answer and must survive — this is only about the UNTOUCHED field.
 *   - `counterText` at render: `null`/`undefined` become an em dash, and
 *     every real number — `0` included — prints itself.
 *
 * `counterText` exists because `{m.bowling?.wickets || 0}` is the bug in
 * its display form: `||` cannot tell `0` from `null`, so it would reprint
 * the same lie the storage fix just removed, and `?? 0` would do it more
 * politely. The dash is the same "not recorded" mark `dateTimeLabel`
 * (#463), the season/team cells and `batting_position` already use on this
 * page, so nothing new has to be learned to read it.
 *
 * NOT for aggregates. Career totals and averages already exclude a match
 * the player did not bat or bowl in (`api/src/routes/statsCompute.ts`
 * filters on balls faced and overs above zero), and #715 says so
 * explicitly: the damage was to the stored record and the list, never to
 * the averages. A tile showing a career total of nothing should read `0`,
 * because nothing IS the total — do not route those through here.
 */

/** What a blank renders as, everywhere. Em dash, not a hyphen. */
export const NOT_RECORDED = "—";

/**
 * Submit-side: the raw string a counter input holds, as a number or `null`.
 *
 * `null` (not `undefined`) because the API distinguishes them — its match
 * PUT is a partial patch and `pruneUndefined` drops an `undefined` before
 * it reaches the merge, which would leave a previously-recorded number in
 * place. Clearing a counter on an edit has to be expressible, and `null` is
 * how the schema (`z.number().nullable()`) spells it.
 */
export function counterOrNull(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (s === "") return null;
  const cleaned = s.replace(/[^\d.\-]/g, "");
  if (cleaned === "" || cleaned === "-" || cleaned === ".") return null;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}

/**
 * Render-side: a counter as text, or the dash when it was never recorded.
 *
 * A recorded `0` returns `"0"`. That is the whole point of the ticket read
 * from the other end: the two states have to look different.
 */
export function counterText(v) {
  if (v === null || v === undefined || v === "") return NOT_RECORDED;
  return String(v);
}

/**
 * A derived rate (strike rate, economy) — `null` when its divisor was never
 * recorded, rather than the `0` the old ternary produced.
 *
 * Same argument as the counters it is built from: a strike rate of 0 means
 * a player faced deliveries and scored off none of them, which is a fact
 * about an innings. It must not also be what the record says about a match
 * in which they never batted.
 */
export function rateOrNull(numerator, denominator, scale = 1) {
  const d = counterOrNull(denominator);
  const n = counterOrNull(numerator);
  if (!d) return null;
  return +(((n ?? 0) / d) * scale).toFixed(2);
}
